'use client'

import { useRef } from 'react'
import Image from 'next/image'
import { motion, useReducedMotion } from 'motion/react'

export interface TestimonialItem {
  name?: string
  role?: string
  quote?: string
  image?: string
  imageAlt?: string
}

interface TestimonialsBentoProps {
  locale: 'en' | 'ja'
  eyebrow: string
  title: string
  description: string
  items: TestimonialItem[]
}

/**
 * Mobile-only inset — alternates left/right for a loose stacked feel when
 * cards collapse to a single column. On lg, cards live in independent
 * flex columns so left/right stacks have their own vertical rhythm
 * (no row coupling = no dead space when one side has a longer quote).
 */
function mobileInset(index: number): string {
  const insets = [
    'max-w-[94%] mr-auto',
    'max-w-[88%] ml-auto',
    'max-w-[92%] mr-auto',
    'max-w-[86%] ml-auto',
  ]
  return `${insets[index % insets.length]} lg:max-w-none lg:mr-0 lg:ml-0`
}

/** Opening quote mark: real student words, so no star ratings (they made the reviews look generated). */
function QuoteMark() {
  return (
    <span
      aria-hidden
      className="block h-8 font-[family-name:var(--font-noto-serif-jp)] text-[56px] font-bold leading-[0.9] text-brand-gold/80"
    >
      &ldquo;
    </span>
  )
}

function TestimonialCard({
  testimonial,
  featured,
  placement,
  reducedMotion,
  delay,
}: {
  testimonial: TestimonialItem
  featured: boolean
  placement: string
  reducedMotion: boolean
  delay: number
}) {
  const quote = testimonial.quote?.replace(/\s+/g, ' ').trim() ?? ''
  const initial = (testimonial.name || '?').charAt(0).toUpperCase()

  return (
    <motion.article
      className={`group relative flex flex-col rounded-[28px] p-7 md:p-9 border border-brand-cool-gray bg-white shadow-[0_12px_40px_-16px_rgba(20,33,61,0.12)] transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:border-brand-gold/40 hover:shadow-[0_20px_48px_-20px_rgba(20,33,61,0.18)] ${placement}`}
      initial={reducedMotion ? false : { y: 16, opacity: 0 }}
      whileInView={reducedMotion ? undefined : { y: 0, opacity: 1 }}
      viewport={{ once: true, margin: '-8%' }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="relative z-[1] flex h-full flex-col">
        <QuoteMark />

        <blockquote className="type-quote mt-3 flex-1 text-[15px] md:text-[17px]">
          {quote}
        </blockquote>

        <footer className="mt-7 flex items-center gap-3 border-t border-brand-cool-gray/70 pt-5">
          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-brand-cool-gray bg-white">
            {testimonial.image ? (
              <Image
                src={testimonial.image}
                alt={testimonial.imageAlt || testimonial.name || 'Student'}
                fill
                sizes="40px"
                className="object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-[13px] font-medium text-brand-navy">
                {initial}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold text-brand-charcoal">
              {testimonial.name}
            </p>
            {testimonial.role && (
              <p className="mt-1 truncate text-[10.5px] uppercase tracking-[0.18em] text-brand-muted">
                {testimonial.role}
              </p>
            )}
          </div>
        </footer>
      </div>
    </motion.article>
  )
}

export default function TestimonialsBento({
  eyebrow,
  title,
  description,
  items,
}: TestimonialsBentoProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const reducedMotion = useReducedMotion()

  const featuredIndex = items.reduce(
    (best, t, i) => {
      const len = (t.quote ?? '').length
      return len > best.len ? { index: i, len } : best
    },
    { index: 0, len: 0 }
  ).index

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="section-padding relative overflow-hidden"
    >
      <div className="section-container relative max-w-6xl space-y-10 md:space-y-14">
        <motion.div
          className="section-header"
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="section-eyebrow section-eyebrow-centered">{eyebrow}</p>
          <h2 className="type-section-title">{title}</h2>
          <p className="type-section-lead mx-auto">{description}</p>
        </motion.div>

        {/* Mobile: linear list with alternating insets */}
        <div className="mx-auto flex max-w-6xl flex-col gap-7 lg:hidden">
          {items.map((t, idx) => (
            <TestimonialCard
              key={`m-${t.name ?? idx}`}
              testimonial={t}
              featured={idx === featuredIndex}
              placement={mobileInset(idx)}
              reducedMotion={!!reducedMotion}
              delay={idx * 0.1}
            />
          ))}
        </div>

        {/* Desktop: two independent flex columns — each side has its own
            vertical rhythm so a long quote on the right never pushes the
            second card on the left downward. */}
        <div className="mx-auto hidden max-w-6xl lg:grid lg:grid-cols-2 lg:gap-7 lg:items-start">
          <div className="flex flex-col gap-10 pt-10">
            {items.map((t, idx) =>
              idx % 2 === 0 ? (
                <TestimonialCard
                  key={`l-${t.name ?? idx}`}
                  testimonial={t}
                  featured={idx === featuredIndex}
                  placement=""
                  reducedMotion={!!reducedMotion}
                  delay={idx * 0.1}
                />
              ) : null
            )}
          </div>
          <div className="flex flex-col gap-10 pt-24">
            {items.map((t, idx) =>
              idx % 2 === 1 ? (
                <TestimonialCard
                  key={`r-${t.name ?? idx}`}
                  testimonial={t}
                  featured={idx === featuredIndex}
                  placement=""
                  reducedMotion={!!reducedMotion}
                  delay={idx * 0.1}
                />
              ) : null
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
